import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Download, Heart, Loader2, Lock } from "lucide-react";
import MiniHeader from "@/components/site/MiniHeader";
import ShareFilmCard from "@/components/site/ShareFilmCard";
import ThankYouNoteForm from "@/components/site/ThankYouNoteForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FilmPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notReady, setNotReady] = useState(false);

  useEffect(() => {
    axios.get(`${API}/film/${token}`).then((r) => setData(r.data)).catch(() => setNotReady(true));
  }, [token]);

  const loaded = !!data;
  useEffect(() => {
    // Arriving from the "leave a few words" email: bring the note form into view
    if (loaded && window.location.hash === "#note") {
      setTimeout(() => document.getElementById("note")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [loaded]);

  if (notReady) {
    return (
      <div className="min-h-screen"><MiniHeader />
        <div data-testid="film-not-ready" className="max-w-xl mx-auto px-6 py-32 text-center">
          <h1 className="serif text-4xl font-light text-[#F5F0E8]">The film isn&apos;t ready yet</h1>
          <p className="mt-4 text-[#A0988E] spectral">We&apos;ll email you the moment it is. Questions? Write to eternallegacytributes@gmail.com.</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#C5A55A]" /></div>;

  const stream = `${API}/film/${token}/stream`;
  return (
    <div data-testid="film-page" className="min-h-screen">
      <MiniHeader right={<span className="hidden sm:inline-flex items-center gap-2 text-xs text-[#A0988E]"><Lock size={13} className="text-[#D4AF37]" /> Private to your family</span>} />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="overline mb-4">In loving memory</div>
        <h1 className="serif text-4xl sm:text-6xl font-light leading-[1.05] text-[#F5F0E8]">
          <span data-testid="film-loved-one-name" className="italic text-[#E6B89C]">{data.loved_one_name}</span>
        </h1>
        <p className="mt-4 text-[#A0988E] spectral">A tribute film, made with care for {data.name} and family.</p>

        <div className="mt-10 rounded-[22px] overflow-hidden border border-[rgba(212,175,55,0.25)] shadow-2xl shadow-black/60 bg-black">
          <video data-testid="film-player" src={stream} controls playsInline preload="metadata" className="w-full aspect-video" />
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <a data-testid="film-download-button" href={`${stream}?download=true`} className="btn-primary">
            <Download size={16} /> Download the film
          </a>
          <span className="text-sm text-[#A0988E]">Keep a copy to share with family or play at the service.</span>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6 items-start">
          <ShareFilmCard token={token} lovedOne={data.loved_one_name} sharePath={data.share_path}
                         onChange={(share_path) => setData((d) => ({ ...d, share_path }))} />
          <div id="note" className="scroll-mt-24 min-w-0">
            <ThankYouNoteForm token={token} familyName={data.name} existing={data.review}
                              onSaved={(review) => setData((d) => ({ ...d, review }))} />
          </div>
        </div>

        <p className="mt-16 text-center text-[#A0988E] spectral italic flex items-center justify-center gap-2">
          <Heart size={14} className="text-[#C5A55A]" /> With our warmest thoughts — Eternal Legacy Tributes
        </p>
      </main>
    </div>
  );
}
